// src/components/tiles/JobSatisfactionSummaryTile.jsx
import React from 'react';
import { Box, Stack, Typography, Divider, Tooltip } from '@mui/material';
import TileShell from './TileShell';
import ScoreBadge from './ScoreBadge';
import ReceiptsList from './ReceiptsList';

export default function JobSatisfactionSummaryTile({
  title,
  avgImportance = 0,
  avgSatisfaction = 0,
  gap = 0,
  histogram = [],              // 11 bins [0..10]
  interviewCount = 0,
  summaryText,
  topPositiveDrivers = [],
  topNegativeDrivers = [],
  representativeQuotes = []
}) {
  const MetricRow = (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
      <ScoreBadge label="Avg importance" value={avgImportance} metricKey="importance" />
      <ScoreBadge label="Avg satisfaction" value={avgSatisfaction} metricKey="satisfaction" />
      <ScoreBadge label="Gap" value={gap} metricKey="gap" />
    </div>
  );

  const bins = Array.isArray(histogram) && histogram.length === 11 ? histogram : new Array(11).fill(0);
  const max = Math.max(1, ...bins);

  return (
    <TileShell
      typePill="overview"
      title={title || 'Summary of Job'}
      metricsRow={MetricRow}
      body={
        <Stack spacing={1.75}>
          {summaryText && (
            <Typography variant="body2" color="text.secondary">{summaryText}</Typography>
          )}

          {/* Histogram with safe spacing + tooltips */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Satisfaction scores breakdown
            </Typography>
            <Box sx={{ display:'flex', alignItems:'flex-end', gap: 0.75, height: 92, mb: 0.5 }}>
              {bins.map((count, i) => {
                const h = Math.round((count / max) * 72); // bar height
                return (
                  <Box key={i} sx={{ textAlign:'center' }}>
                    <Tooltip title={`${count} interview${count === 1 ? '' : 's'}`} arrow>
                      <Box sx={{
                        width: 10,
                        height: h,
                        bgcolor: 'divider',
                        borderRadius: 0.5,
                        transition: 'opacity 120ms',
                        '&:hover': { opacity: 0.8 }
                      }} />
                    </Tooltip>
                    <Typography variant="caption" sx={{ display:'block', mt: 0.5 }}>
                      {i}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {(topPositiveDrivers?.length || topNegativeDrivers?.length) ? (
            <>
              <Divider />
              <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
                {topPositiveDrivers?.length > 0 && (
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="subtitle2">Top positive drivers</Typography>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {topPositiveDrivers.slice(0,3).map((d, i) =>
                        <li key={i}><Typography variant="body2">{d}</Typography></li>
                      )}
                    </ul>
                  </Box>
                )}
                {topNegativeDrivers?.length > 0 && (
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="subtitle2">Top negative drivers</Typography>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {topNegativeDrivers.slice(0,3).map((d, i) =>
                        <li key={i}><Typography variant="body2">{d}</Typography></li>
                      )}
                    </ul>
                  </Box>
                )}
              </Box>
            </>
          ) : null}

          {representativeQuotes?.length > 0 && (
            <>
              <Divider />
              <ReceiptsList receipts={representativeQuotes} />
            </>
          )}
        </Stack>
      }
    />
  );
}
